import { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import PageHead from "../components/PageHead";
import ParticleField from "../components/ParticleField";
import Reveal from "../components/Reveal";
import { WhatsAppButton, PrimaryButton } from "../components/Buttons";
import { heroBgStyle } from "../lib/heroBg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

function validate(values) {
  const errors = {};
  if (!values.full_name.trim() || values.full_name.trim().length < 2)
    errors.full_name = "Please enter your full name.";
  if (!/^\S+@\S+\.\S+$/.test(values.email)) errors.email = "Enter a valid email address.";
  if (!values.phone.trim() || values.phone.trim().length < 5)
    errors.phone = "Enter a valid phone number.";
  if (!values.subject.trim() || values.subject.trim().length < 2)
    errors.subject = "Please add a subject.";
  if (!values.message.trim() || values.message.trim().length < 5)
    errors.message = "Tell us a little about your enquiry.";
  return errors;
}

export default function Contact() {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onChange = (e) => {
    setValues((v) => ({ ...v, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus({ state: "submitting", msg: "" });
    try {
      await axios.post(`${API}/contact`, values);
      setStatus({
        state: "success",
        msg: "Thank you — your message has landed. We'll respond within 24 hours.",
      });
      setValues(initial);
    } catch (err) {
      console.error(err);
      setStatus({
        state: "error",
        msg: "Something went wrong sending your message. Please try again or message us on WhatsApp.",
      });
    }
  };

  return (
    <main data-testid="contact-page">
      <PageHead
        title="Contact | Digital Archdeacon Media Ltd"
        description="Reach out to Digital Archdeacon Media — call, email, or message us on WhatsApp from anywhere in Africa or beyond."
      />

      <section className="relative pt-36 pb-12 overflow-hidden" style={heroBgStyle} data-testid="contact-hero">
        <ParticleField density={45} />
        <div className="absolute inset-0 bg-grid opacity-25 pointer-events-none" />
        <div className="container-x relative">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Contact us</span>
            <h1 className="h-display text-[clamp(3rem,8vw,7rem)] mt-3">
              Let's start <span className="text-gradient-blue">something great</span>.
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#CBD5E1]">
              We'd love to hear from you. Reach out and let's make it happen.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="bg-navy-gradient">

      <section className="pb-12 pt-4" data-testid="contact-main">
        <div className="container-x grid lg:grid-cols-2 gap-10">
          {/* Form */}
          <Reveal>
            <form onSubmit={onSubmit} noValidate className="glass p-8 md:p-10" data-testid="contact-form">
              <h2 className="font-heading text-2xl font-semibold">Send a message</h2>
              <p className="text-[#CBD5E1] text-sm mt-2">All fields marked * are required.</p>

              <div className="mt-7 grid gap-5">
                {[
                  { name: "full_name", label: "Full Name *", type: "text" },
                  { name: "email", label: "Email Address *", type: "email" },
                  { name: "phone", label: "Phone Number *", type: "tel" },
                  { name: "subject", label: "Subject *", type: "text" },
                ].map((f) => (
                  <div key={f.name} className={`float-field ${errors[f.name] ? "field-error" : ""}`}>
                    <input
                      id={`c-${f.name}`}
                      name={f.name}
                      type={f.type}
                      value={values[f.name]}
                      onChange={onChange}
                      placeholder=" "
                      autoComplete="off"
                      data-testid={`contact-${f.name}`}
                    />
                    <label htmlFor={`c-${f.name}`}>{f.label}</label>
                    {errors[f.name] ? (
                      <p className="error-text" data-testid={`contact-error-${f.name}`}>{errors[f.name]}</p>
                    ) : null}
                  </div>
                ))}

                <div className={`float-field ${errors.message ? "field-error" : ""}`}>
                  <textarea
                    id="c-message"
                    name="message"
                    value={values.message}
                    onChange={onChange}
                    placeholder=" "
                    data-testid="contact-message"
                  />
                  <label htmlFor="c-message">Message *</label>
                  {errors.message ? (
                    <p className="error-text" data-testid="contact-error-message">{errors.message}</p>
                  ) : null}
                </div>
              </div>

              <button
                type="submit"
                disabled={status.state === "submitting"}
                data-testid="contact-submit"
                className="glow-btn mt-8 inline-flex items-center justify-center rounded-full bg-electric px-8 py-4 text-sm font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status.state === "submitting" ? "Sending…" : "Send Message"}
              </button>

              {status.state === "success" ? (
                <p data-testid="contact-success" className="mt-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 px-4 py-3 text-sm">{status.msg}</p>
              ) : null}
              {status.state === "error" ? (
                <p data-testid="contact-failure" className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 px-4 py-3 text-sm">{status.msg}</p>
              ) : null}
            </form>
          </Reveal>

          {/* Details Card (no socials) */}
          <Reveal delay={120}>
            <aside className="glass p-8 md:p-10 h-full" data-testid="contact-details">
              <h2 className="font-heading text-2xl font-semibold">Get in touch</h2>
              <p className="text-[#CBD5E1] text-sm mt-2">Our team is ready to help — choose what works for you.</p>

              <ul className="mt-7 space-y-5 text-[15px]">
                <li className="flex items-start gap-4">
                  <span className="mt-0.5 grid place-items-center h-10 w-10 rounded-xl bg-electric/15 text-electric">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#CBD5E1]">Office</p>
                    <p className="text-white mt-1">6 Edwin Amadi Street, One Day, Enugu, Nigeria</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="mt-0.5 grid place-items-center h-10 w-10 rounded-xl bg-electric/15 text-electric">
                    <Mail className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#CBD5E1]">Email</p>
                    <a href="mailto:info@digitalarchdeacon.com" className="text-white mt-1 inline-block hover:text-electric transition-colors">
                      info@digitalarchdeacon.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="mt-0.5 grid place-items-center h-10 w-10 rounded-xl bg-electric/15 text-electric">
                    <Phone className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#CBD5E1]">Phone</p>
                    <a href="tel:+2348147276648" className="text-white mt-1 inline-block hover:text-electric transition-colors">
                      +234-814-727-6648
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="mt-0.5 grid place-items-center h-10 w-10 rounded-xl bg-gold/15 text-gold">
                    <Clock className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#CBD5E1]">Hours</p>
                    <p className="text-white mt-1">Mon – Fri · 9:00am – 6:00pm WAT</p>
                  </div>
                </li>
              </ul>

              <div className="mt-8">
                <WhatsAppButton testId="contact-whatsapp" />
              </div>
            </aside>
          </Reveal>
        </div>
      </section>

      {/* Unique CTA: 24-hour promise */}
      <section className="section-pad" data-testid="contact-cta">
        <div className="container-x">
          <Reveal>
            <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-cta-gradient p-12 md:p-14 text-center">
              <div className="absolute -top-32 -right-20 h-80 w-80 rounded-full bg-electric/40 blur-3xl" />
              <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Our 24-hour promise</span>
              <h2 className="h-display text-4xl md:text-5xl mt-3">
                Send us a brief — sleep on it — wake up to a proposal.
              </h2>
              <p className="mt-5 max-w-2xl mx-auto text-[#CBD5E1] text-lg">
                Every quote request gets a real human reply within one business day. No bots, no boilerplate, no chase-ups.
              </p>
              <div className="mt-9 flex justify-center">
                <PrimaryButton to="/request-quote" testId="contact-cta-quote">
                  Request a Free Quote
                </PrimaryButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      </div>
    </main>
  );
}
